import { RadialBarChart, RadialBar, ResponsiveContainer, Cell } from "recharts";
import { healthData } from "../../data/mock";
import { total, type ExecutiveTransaction } from "../../data/executiveOverview";

function ScoreRing({ score }: { score: number }) {
  const pct = score / 100;
  const size = 140;
  const radius = 56;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ position: "absolute", inset: 0 }}>
        {/* Track */}
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="var(--border)" strokeWidth="14" />
        {/* Progress */}
        <circle
          cx={size/2} cy={size/2} r={radius}
          fill="none"
          stroke="#6b3fa0"
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={`${2 * Math.PI * radius}`}
          strokeDashoffset={`${2 * Math.PI * radius * (1 - pct)}`}
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: "stroke-dashoffset 0.8s ease" }}
        />
      </svg>
      <div style={{
        position: "absolute", top: 0, left: 0, width: size, height: size,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        pointerEvents: "none"
      }}>
        <span style={{ fontSize: 26, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>/ 100</span>
      </div>
    </div>
  );
}

function MiniBar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-primary)" }}>{value}%</span>
      </div>
      <div style={{ height: 5, background: "var(--border)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${value}%`, background: color, borderRadius: 3, transition: "width 0.7s ease" }} />
      </div>
    </div>
  );
}

export function ProcurementHealth({ rows }: { rows?: ExecutiveTransaction[] }) {
  const spend = rows ? total(rows, "Spend_Amount_USD") : 0;
  const share = (members: ExecutiveTransaction[]) => spend ? total(members, "Spend_Amount_USD") / spend * 100 : 0;
  const managed = rows ? share(rows.filter((row) => row.Managed_Spend_Flag === "Yes")) : 0;
  const classified = rows ? share(rows.filter((row) => row.Classification_Status === "Classified")) : 0;
  const contracted = rows ? share(rows.filter((row) => row.Contract_Status === "Contracted")) : 0;
  const score = rows ? Math.round((managed + classified + contracted) / 3) : healthData.score;
  const label = rows ? score >= 80 ? "Good" : score >= 60 ? "Fair" : "Needs Attention" : healthData.label;
  return (
    <div className="health-panel">
      <div className="health-panel__title">Procurement Health</div>
      <div className="health-panel__sub">Overall performance composite score</div>

      <div className="health-score-area">
        <ScoreRing score={score} />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#6b3fa0", marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{rows ? `${rows.length} transactions` : "vs. 72 last quarter"}</div>
        </div>
      </div>

      <div style={{
        marginTop: 16,
        display: 'flex',
        gap: 32,
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      }}>
        <div style={{ textAlign: 'left', minWidth: 140 }}>
          <div style={{ fontSize: 12, color: '#6A7282', fontWeight: 400, letterSpacing: 0.2 }}>SPEND UNDER MGMT</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#0A0A0A', marginTop: 4 }}>{rows ? `${managed.toFixed(1)}%` : "-"}</div>
        </div>
        <div style={{ textAlign: 'left', minWidth: 140 }}>
          <div style={{ fontSize: 12, color: '#6A7282', fontWeight: 400, letterSpacing: 0.2 }}>CLASSIFICATION</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#0A0A0A', marginTop: 4 }}>{rows ? `${classified.toFixed(1)}%` : "94.2%"}</div>
        </div>
      </div>
    </div>
  );
}
